/**
 * The decision boundary: what goes out to JEV, what comes back, what happens
 * when nothing comes back, and the record every move leaves behind.
 *
 * The questions are typed - one Choice for the plan's first step, one Score for
 * the plan's confidence, one Noul for the risk of closing a loop - and the
 * model's probabilities are carried through untouched, because the page shows
 * them. When the model is unreachable the decision falls back to the planner's
 * own preference, labelled as exactly that: a template answer is never dressed
 * up as a model answer.
 */

import {
  asNumber,
  asString,
  DecodeError,
  field,
  optionalField,
  parseJson,
  type JsonObject,
  type JsonValue,
} from "../json";
import { DIRECTIONS, features, type Arena, type Direction } from "./arena";
import type { GuardOutcome } from "./guard";
import type { Candidate } from "./plan";

/** The indexer's view of the brain, reduced to what one decision needs. */
export type ThoughtState = {
  readonly tick: number;
  /** Absent until the indexer decodes the row; never invented. */
  readonly stateRoot: string | null;
  readonly fired: number;
  readonly spiked: number;
  readonly digest: string;
  readonly bias: Direction;
  /** True when this block carried no fresh thought and the last one was reused. */
  readonly reused: boolean;
};

export type Decision = {
  /** The plan's first step: the direction the model answered for. */
  readonly direction: Direction;
  readonly probabilities: Readonly<Record<Direction, number>>;
  readonly confidence: number;
  /** The score question's own top value: 1 when the model answered 0..1, 2 for 0..2. */
  readonly confidenceScale: number;
  readonly modelConfidence: number;
  readonly risk: number;
};

/**
 * Who decided, in the record's own words. `policy` is the local engine - the
 * planner's policy wearing the typed-decision shape, no network and no cost;
 * `jev` is the live model; `jev-failed` is the model asked and not answering,
 * with the local engine's plan labelled as such.
 */
export type DecisionSource =
  | { readonly kind: "policy"; readonly engine: string }
  | { readonly kind: "jev"; readonly model: string; readonly latencyMs: number }
  | { readonly kind: "jev-failed"; readonly reason: string };

export type DecidedPlan = {
  readonly decision: Decision;
  readonly source: DecisionSource;
  readonly candidates: readonly Candidate[];
};

export type BlockRef = {
  readonly number: number;
  readonly hash: string;
  readonly timestamp: number;
  readonly txs: number;
};

export type MoveRecord = {
  readonly move: number;
  readonly block: BlockRef;
  readonly thought: ThoughtState | null;
  readonly features: ReturnType<typeof features>;
  readonly decision: Decision;
  readonly applied: Direction;
  readonly guard: GuardOutcome;
  /** The room the plan's first step would have left, and the best a rescue could keep. */
  readonly room: { readonly proposed: number; readonly best: number };
  /** Where this move sits in its plan: 1 is the step the model answered for. */
  readonly slot: number;
  readonly planSize: number;
  readonly source: DecisionSource;
};

/** The model the launchpad already talks to. */
export const JEV_MODEL = "jev-latest";

/** What an injected transport answers with: the body and the status, nothing else. */
export type HttpResponse = {
  readonly ok: boolean;
  readonly status: number;
  readonly text: () => Promise<string>;
};

/**
 * The one request decidePlan may make. The caller owns the endpoint and the
 * credential: the page holds no key, and nothing here chooses where a body goes.
 */
export type Post = (body: string) => Promise<HttpResponse>;

const DIGEST_SEED = 7;

const DIGEST_BASE = 31;

const PROBABILITY_FLOOR = 1;

/** The fired set folded into one value and one direction. */
export type FiredDigest = {
  readonly hex: string;
  readonly bias: Direction;
};

/**
 * The fired neurons folded into one value and one direction. Deterministic:
 * the same tick always gives the same digest, and the digest only ever breaks
 * ties - it never overrules a fatal direction.
 */
export function firedDigest(fired: readonly number[]): FiredDigest {
  let hash = DIGEST_SEED;

  for (const id of fired) hash = (Math.imul(hash, DIGEST_BASE) + (id | 0)) >>> 0;

  return { hex: `0x${hash.toString(16).padStart(8, "0")}`, bias: DIRECTIONS[hash % 4] };
}

/** What one candidate plan promises, in the model's own criterion text. */
function promise(candidate: Candidate, steps: number): string {
  if (candidate.fatal) return "the head would step into a wall or its own body: the run ends here";

  const apples = candidate.eats === 1 ? "eats 1 apple" : `eats ${candidate.eats} apples`;
  const end = candidate.ends === 0 ? "ends on the apple" : `ends ${candidate.ends} cells from it`;

  return `${candidate.steps.length} of ${steps} steps, ${apples}, ${end}, ${candidate.free} free cells left after the first step`;
}

/**
 * The typed questions for one block: which first step, how confident, and
 * whether the plan would close a loop. Each criterion is a whole plan, so the
 * model answers the direction and reads what follows from it.
 */
export function jevRequest(input: {
  readonly arena: Arena;
  readonly candidates: readonly Candidate[];
  readonly thought: ThoughtState;
  readonly move: number;
  readonly steps: number;
}): JsonObject {
  const seen = features(input.arena);
  const criteria: JsonObject = {};

  for (const candidate of input.candidates) {
    criteria[candidate.direction] = promise(candidate, input.steps);
  }

  return {
    model: JEV_MODEL,
    state: {
      context: `move ${input.move} of a snake arena whose opponent is the sealed BANC v888 fly brain on Arc; the arena is a simulation, the brain state below is a real onchain thought, and the code picks one direction per plan of ${input.steps} steps`,
      arena: {
        length: seen.length,
        free_space_ratio: Number(seen.freeSpaceRatio.toFixed(4)),
        food: { dx: seen.food.dx, dy: seen.food.dy },
        immediate_danger: [...seen.danger],
        safe_directions: [...seen.safe],
      },
      plans: input.candidates.map((candidate) => ({
        direction: candidate.direction,
        steps: [...candidate.steps],
        eats: candidate.eats,
        ends: candidate.ends,
        free_after_first_step: candidate.free,
        legal: !candidate.fatal,
      })),
      brain: {
        tick: input.thought.tick,
        state_root: input.thought.stateRoot ?? "",
        fired_count: input.thought.fired,
        spiked: input.thought.spiked,
        digest: input.thought.digest,
        digest_bias: input.thought.bias,
      },
    },
    questions: {
      direction: {
        type: "choice",
        instructions: `Step ${input.move}, the first step of the plan that will be played. Each criterion is that first step, with what the rest of the plan does after it. Directions named in immediate_danger end the run.`,
        criteria,
      },
      confidence: {
        type: "score",
        instructions: "How confident the chosen plan is, given the arena and the brain state",
        criteria: [
          "unsure: the plans are near enough to each other",
          "steady: the chosen plan follows the food and keeps the room",
          "certain: one plan is clearly ahead and the others are traps",
        ],
      },
      cycle_risk: {
        type: "noul",
        instructions:
          "The chosen plan would close a loop around the body and trap the snake before this plan ends",
      },
    },
  };
}

/** One decoded reply from the model. */
export type ParsedJev = {
  readonly decision: Decision;
  readonly model: string;
};

/**
 * Reads one real JEV reply. Anything that is not a usable choice throws, so the
 * caller falls back visibly instead of acting on a half-read answer.
 */
export function parseJevAnswer(payload: JsonValue): ParsedJev {
  const model = asString(field(payload, "model", "jev"), "jev.model");
  const answers = field(payload, "answers", "jev");
  const choice = readChoice(field(answers, "direction", "jev.answers"));
  const score = readScore(optionalField(answers, "confidence", "jev.answers"));

  return {
    model,
    decision: {
      direction: choice.direction,
      probabilities: choice.probabilities,
      confidence: score.value,
      confidenceScale: score.scale,
      modelConfidence: choice.confidence,
      risk: readNumberIn(optionalField(answers, "cycle_risk", "jev.answers"), "noul"),
    },
  };
}

/** One direction answer: the choice, its probabilities and its own confidence. */
type ChosenDirection = {
  readonly direction: Direction;
  readonly probabilities: Record<Direction, number>;
  readonly confidence: number;
};

function readChoice(answer: JsonValue): ChosenDirection {
  const label = "jev.answers.direction";
  const kind = asString(field(answer, "type", label), `${label}.type`);

  if (kind !== "choice") throw new DecodeError(`${label}: expected a choice, got "${kind}"`);

  const picked = asString(field(answer, "choice", label), `${label}.choice`);

  if (!isDirection(picked)) throw new DecodeError(`${label}: unknown direction "${picked}"`);

  const raw = field(answer, "probabilities", label);
  const probabilities: Record<Direction, number> = { up: 0, down: 0, left: 0, right: 0 };

  for (const direction of DIRECTIONS) {
    const value = optionalField(raw, direction, `${label}.probabilities`);

    if (value !== null) probabilities[direction] = asNumber(value, `${label}.${direction}`);
  }

  return { direction: picked, probabilities, confidence: readNumberIn(answer, "confidence") };
}

/** A score answer's value and the top of the scale its legend declares. */
type ScoreAmount = {
  readonly value: number;
  readonly scale: number;
};

/** The score answer, with the scale its own legend declares. */
function readScore(answer: JsonValue): ScoreAmount {
  if (answer === null) return { value: 0, scale: 1 };

  const legend = optionalField(answer, "legend", "jev.answers.confidence");
  const scale = isJsonObject(legend) ? Math.max(1, Object.keys(legend).length - 1) : 1;

  return { value: readNumberIn(answer, "score"), scale };
}

function isJsonObject(value: JsonValue): value is JsonObject {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** One numeric member of an answer, or zero when the model left it out. */
function readNumberIn(answer: JsonValue, member: string): number {
  if (answer === null) return 0;

  const value = optionalField(answer, member, "jev.answers");

  return value === null ? 0 : asNumber(value, `jev.answers.${member}`);
}

function isDirection(value: string): value is Direction {
  return DIRECTIONS.some((known) => known === value);
}

/**
 * The policy engine: the planner's own policy wearing the model's shape - a
 * direction with a probability for every criterion, a score, a model confidence
 * and the risk of closing a loop. Deterministic, offline, and always labelled
 * `policy`: a local answer is never presented as a live one.
 *
 * The probabilities are the planner's own preference softened over the
 * candidates; the risk is how much room the chosen step gives up against the
 * best step available, which is exactly what the reference's cycle shield
 * watches.
 */
export function policyDecision(input: {
  readonly candidates: readonly Candidate[];
  readonly bias: Direction;
}): Decision {
  const weights: Record<Direction, number> = { up: 0, down: 0, left: 0, right: 0 };
  let total = 0;

  for (const candidate of input.candidates) {
    const weight = candidate.fatal ? 0 : PROBABILITY_FLOOR + Math.max(0, candidate.score);

    weights[candidate.direction] = weight;
    total += weight;
  }

  let direction: Direction | null = null;
  let best = Number.NEGATIVE_INFINITY;
  const probabilities: Record<Direction, number> = { up: 0, down: 0, left: 0, right: 0 };

  for (const candidate of input.candidates) {
    probabilities[candidate.direction] = total > 0 ? weights[candidate.direction] / total : 0;

    if (candidate.fatal) continue;

    if (
      candidate.score > best ||
      (candidate.score === best && candidate.direction === input.bias)
    ) {
      direction = candidate.direction;
      best = candidate.score;
    }
  }

  const pick = input.candidates.find((candidate) => candidate.direction === direction);

  const room = input.candidates.reduce(
    (most, candidate) => (candidate.fatal ? most : Math.max(most, candidate.free)),
    0,
  );

  const second = input.candidates
    .filter((candidate) => !candidate.fatal && candidate.direction !== direction)
    .reduce((score, candidate) => Math.max(score, candidate.score), Number.NEGATIVE_INFINITY);

  const margin =
    pick === undefined || second === Number.NEGATIVE_INFINITY ? 0 : pick.score - second;

  const risk = pick === undefined || room === 0 ? 0 : clamp01(1 - pick.free / room);

  return {
    direction: direction ?? DIRECTIONS[0],
    probabilities,
    confidence: clamp01(0.5 + margin / 4),
    confidenceScale: 1,
    modelConfidence: clamp01(0.5 + margin / 4),
    risk,
  };
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

/**
 * One plan: the model when it answers, the labelled template when it does not.
 * Never more than one request per call, and the request carries no key.
 */
export async function decidePlan(input: {
  readonly candidates: readonly Candidate[];
  readonly arena: Arena;
  readonly thought: ThoughtState;
  readonly move: number;
  readonly steps: number;
  readonly bias: Direction;
  readonly post: Post;
  readonly now?: () => number;
}): Promise<DecidedPlan> {
  const request = jevRequest({
    arena: input.arena,
    candidates: input.candidates,
    thought: input.thought,
    move: input.move,
    steps: input.steps,
  });

  const now = input.now ?? (() => Date.now());
  const started = now();

  try {
    const response = await input.post(JSON.stringify(request));

    if (!response.ok) throw new Error(`the model answered HTTP ${response.status}`);

    const { decision, model } = parseJevAnswer(parseJson(await response.text(), "jev"));

    return {
      decision,
      candidates: input.candidates,
      source: { kind: "jev", model, latencyMs: Math.max(0, now() - started) },
    };
  } catch (error) {
    const reason = error instanceof Error ? error.message : "JEV unreachable";

    return {
      decision: policyDecision({ candidates: input.candidates, bias: input.bias }),
      candidates: input.candidates,
      source: { kind: "jev-failed", reason },
    };
  }
}

/** The first step of a plan is the model's own answer; the rest continue it. */
export function stepRole(record: MoveRecord): "chosen" | "follow-through" {
  return record.slot === 1 ? "chosen" : "follow-through";
}

export type RecordRow = {
  readonly block: string;
  readonly tick: string;
  readonly reused: boolean;
  /** The shortened state root, or null while the indexer has not decoded it. */
  readonly root: string | null;
  readonly move: Direction;
  readonly top: string;
  readonly slot: string;
  /** Who decided this step: the live model, the plan it chose, or the local engine. */
  readonly source: "jev" | "plan" | "policy";
  /** Null when no model answered, so the view never prints a fake latency. */
  readonly latencyMs: number | null;
  readonly guard: GuardOutcome;
};

/** The row a move leaves in the trail: numbers and rulings, no prose. */
export function recordRow(record: MoveRecord): RecordRow {
  const chosen = stepRole(record) === "chosen";

  return {
    block: record.block.number.toLocaleString("en-US"),
    tick: (record.thought?.tick ?? 0).toLocaleString("en-US"),
    reused: record.thought?.reused ?? false,
    root: record.thought?.stateRoot == null ? null : shortHex(record.thought.stateRoot),
    move: record.applied,
    top: topProbability(record.decision).toFixed(2),
    slot: `${record.slot}/${record.planSize}`,
    source: sourceOf(record, chosen),
    latencyMs: record.source.kind === "jev" ? record.source.latencyMs : null,
    guard: record.guard,
  };
}

/** A step's source: the model's own step, its plan's continuation, or the engine. */
function sourceOf(record: MoveRecord, chosen: boolean): "jev" | "plan" | "policy" {
  if (record.source.kind === "jev") return chosen ? "jev" : "plan";

  return "policy";
}

function topProbability(decision: Decision): number {
  return DIRECTIONS.reduce((top, direction) => Math.max(top, decision.probabilities[direction]), 0);
}

/** 0x1234…abcd: enough to recognize the row, short enough for a table. */
export function shortHex(value: string): string {
  const body = value.startsWith("0x") ? value.slice(2) : value;

  if (body.length <= 10) return value;

  return `0x${body.slice(0, 4)}…${body.slice(-4)}`;
}
